import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import {
	faCircleCheck,
	faCircleExclamation,
	faLock,
	faPenLine,
	faSpinnerThird,
} from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";

import { CampaignSaveStatus } from "../utils/useCampaignAutosave";

export interface CampaignSaveStatusChipProps {
	status: CampaignSaveStatus;
}

/** `14:32` in the viewer's locale — the chip only ever shows the time, never the date. */
const formatSavedAt = (lastSavedAt: number): string =>
	new Date(lastSavedAt).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

/**
 * CampaignSaveStatusChip — what the campaign builder tells the user about their work (PREC-4151).
 *
 * The builder has no Save button, so the only thing that ever reported a write was a transient
 * "Saving..." snackbar: an edit still in the debounce looked exactly like an edit that had landed,
 * and a save the server refused looked like one too — a rejected mutation reaches the user through
 * nothing at all, because `rtkRejectQueryMiddleware` only redirects on rejected queries. This chip
 * is that report, and it is deliberately persistent: the failure mode this ticket fixed (autosave
 * paused for the rest of the session) was invisible precisely because nothing on screen claimed
 * anything about saving.
 */
export const CampaignSaveStatusChip: FunctionComponent<CampaignSaveStatusChipProps> = ({
	status,
}) => {
	const { t } = useTranslation("translation", { keyPrefix: "list.campaignBuilder" });

	// A deleted campaign is being left (PREC-4258); a toast says why, the chip claims nothing.
	if (status.state === "idle" || status.state === "deleted") {
		return <></>;
	}

	// The structure lock refused a change to the steps (PREC-4616): the stored steps are back, and
	// there is nothing to retry.
	if (status.state === "locked") {
		return (
			<div
				className="flex flex-row items-center gap-2 py-1 px-2.5 rounded-full bg-status-error-light text-status-error"
				data-testid="campaign-save-status"
				data-state="locked"
			>
				<Icon className="w-3.5 h-3.5 shrink-0" icon={faLock} />
				<span className="text-xs font-semibold">{t("saveStructureLocked")}</span>
			</div>
		);
	}

	if (status.state === "error") {
		return (
			<div
				className="flex flex-row items-center gap-2 py-1 px-2.5 rounded-full bg-status-error-light text-status-error whitespace-nowrap"
				data-testid="campaign-save-status"
				data-state="error"
			>
				<Icon className="w-3.5 h-3.5" icon={faCircleExclamation} />
				<span className="text-xs font-semibold">{t("saveFailed")}</span>
				<button
					className="text-xs font-semibold underline underline-offset-2 cursor-pointer"
					data-testid="campaign-save-retry"
					onClick={status.retry}
					type="button"
				>
					{t("saveRetry")}
				</button>
			</div>
		);
	}

	const { icon, label, spin } =
		status.state === "saving"
			? { icon: faSpinnerThird, label: t("saving"), spin: true }
			: status.state === "unsaved"
				? { icon: faPenLine, label: t("saveUnsaved"), spin: false }
				: {
						icon: faCircleCheck,
						label: status.lastSavedAt
							? t("savedAt", { time: formatSavedAt(status.lastSavedAt) })
							: t("saved"),
						spin: false,
					};

	return (
		<div
			className="flex flex-row items-center gap-2 text-text-meta whitespace-nowrap"
			data-testid="campaign-save-status"
			data-state={status.state}
		>
			<Icon
				className={`w-3.5 h-3.5 ${status.state === "saved" ? "text-status-success" : ""} ${
					spin ? "animate-spin" : ""
				}`}
				icon={icon}
			/>
			<span className="text-xs">{label}</span>
		</div>
	);
};
